// src/pages/RoleMaster-page/RoleMaster.js
import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import {
  MdEdit,
  MdDelete,
  MdAdd,
  MdSearch,
  MdClear,
  MdRefresh,
  MdVisibility,
  MdFileDownload,
  MdSecurity,
  MdCheckCircle,
  MdAdminPanelSettings
} from 'react-icons/md';
import {
  HiOutlineViewGrid,
  HiOutlineViewList,
  HiOutlineShieldCheck,
  HiOutlineKey,
  HiOutlineUserGroup
} from 'react-icons/hi';
import { FaCog, FaTruck, FaTools, FaChartBar, FaTimes, FaCheck, FaShieldAlt } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BASE_URL from '../../api';

const API_BASE_URL = `${BASE_URL}/api`;

/* --- Menu Definitions --- */
const dropdownSections = [
  {
    id: 'masters',
    title: 'Masters',
    icon: FaCog,
    items: [
      { label: 'User Master' },
      { label: 'Role Master' },
      { label: 'Customer Master' },
      { label: 'Route Master' },
      { label: 'Customer Price Setting' },
      { label: 'State Master' },
      { label: 'Area Master' },
      { label: 'Pincode Master' },
      { label: 'Branch Master' },
      { label: 'Transport Master' },
    ],
  },
  {
    id: 'manifest',
    title: 'Manifest',
    icon: FaTruck,
    items: [{ label: 'Outgoing Manifest' }, { label: 'Incoming Manifest' }],
  },
  {
    id: 'transaction',
    title: 'Transaction',
    icon: FaTools,
    items: [{ label: 'Docket Confirmation' }, { label: 'Invoice' }, { label: 'Collection' }],
  },
  {
    id: 'reports',
    title: 'Reports',
    icon: FaChartBar,
    items: [
      { label: 'Manifest Report' },
      { label: 'Runsheet Report' },
      { label: 'Delivery Report' },
      { label: 'MIS Report' },
    ],
  },
];

const STATIC_MENUS = [
  'Dashboard',
  'Booking',
  'Pick In',
  'Route',
  'Runsheet',
  'Tracking No',
  'Customer Dashboard',
  'Tracking Page',
];

const initialForm = { roleName: '', description: '', menuPermissions: {} };

const RoleMaster = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingRole, setViewingRole] = useState(null);
  const [viewingPermissions, setViewingPermissions] = useState({});
  const [editingId, setEditingId] = useState(null);

  // View state
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);

  const [form, setForm] = useState(initialForm);

  const token = () => localStorage.getItem('token');
  const headers = () => ({
    'Content-Type': 'application/json',
    ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
  });

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/role/roles`, { headers: headers() });
      const json = await res.json();
      setRoles(json.data || []);
    } catch (err) {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token()) fetchRoles();
  }, []);

  /* --- Toggle Logic for Single Row --- */
  const handleToggleRow = (menuName, isChecked) => {
    setForm((prev) => ({
      ...prev,
      menuPermissions: {
        ...prev.menuPermissions,
        [menuName]: {
          view: isChecked,
          add: isChecked,
          edit: isChecked,
          delete: isChecked,
        },
      },
    }));
  };

  const handleActionChange = (menu, action, checked) => {
    setForm((prev) => ({
      ...prev,
      menuPermissions: {
        ...prev.menuPermissions,
        [menu]: { ...(prev.menuPermissions[menu] || {}), [action]: checked },
      },
    }));
  };

  /* --- Render Single Menu Row inside Form --- */
  const renderMenuRow = (menuName) => {
    const perms = form.menuPermissions[menuName] || {};
    const isAllSelected = perms.view && perms.add && perms.edit && perms.delete;

    return (
      <div
        key={menuName}
        className="flex flex-col md:flex-row md:items-center justify-between p-3 bg-white rounded-lg border border-gray-200 shadow-sm hover:border-blue-400 transition-all"
      >
        <div className="flex items-center gap-3 mb-2 md:mb-0">
          <input
            type="checkbox"
            checked={!!isAllSelected}
            onChange={(e) => handleToggleRow(menuName, e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300 cursor-pointer"
            title="Select all actions for this menu"
          />
          <span className="font-bold text-gray-800 text-xs sm:text-sm">{menuName}</span>
        </div>

        <div className="flex flex-wrap gap-3.5 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
          {['view', 'add', 'edit', 'delete'].map((action) => (
            <label key={action} className="flex items-center gap-1.5 cursor-pointer group">
              <input
                type="checkbox"
                checked={!!perms[action]}
                onChange={(e) => handleActionChange(menuName, action, e.target.checked)}
                className="w-3.5 h-3.5 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
              />
              <span className="text-[10px] uppercase font-bold text-gray-500 group-hover:text-blue-600">
                {action}
              </span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  /* --- Filter & Pagination --- */
  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    return roles.filter(
      (r) =>
        !term ||
        r.roleName?.toLowerCase().includes(term) ||
        r.description?.toLowerCase().includes(term)
    );
  }, [roles, search]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  // Modal Open
  const openModal = async (role = null) => {
    if (role) {
      setEditingId(role._id);
      try {
        const res = await fetch(`${API_BASE_URL}/role/menu-access/${role._id}`, { headers: headers() });
        const json = await res.json();
        setForm({
          roleName: role.roleName || '',
          description: role.description || '',
          menuPermissions: json.menuPermissions || {},
        });
      } catch (e) {
        console.error(e);
      }
    } else {
      setEditingId(null);
      setForm(initialForm);
    }
    setModalOpen(true);
  };

  // Open View Details Modal
  const openViewModal = async (role) => {
    setViewingRole(role);
    setViewingPermissions({});
    setViewModalOpen(true);
    try {
      const res = await fetch(`${API_BASE_URL}/role/menu-access/${role._id}`, { headers: headers() });
      const json = await res.json();
      setViewingPermissions(json.menuPermissions || {});
    } catch (e) {
      console.error(e);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.roleName.trim()) {
      toast.error('Role Name is required');
      return;
    }

    setLoading(true);
    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId ? `${API_BASE_URL}/role/roles/${editingId}` : `${API_BASE_URL}/role/roles`;
      const roleRes = await fetch(url, {
        method,
        headers: headers(),
        body: JSON.stringify({ roleName: form.roleName, description: form.description }),
      });
      const roleData = await roleRes.json();
      const roleId = editingId || roleData.data?._id;

      await fetch(`${API_BASE_URL}/role/menu-access`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          roleId,
          roleName: form.roleName,
          menuPermissions: form.menuPermissions,
        }),
      });

      toast.success(editingId ? 'Role updated successfully' : 'Role created successfully');
      fetchRoles();
      setModalOpen(false);
    } catch (err) {
      toast.error('Failed to save role and permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, roleName) => {
    const result = await Swal.fire({
      title: 'Delete Role?',
      text: `Are you sure you want to remove the role "${roleName}"? This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete role',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      borderRadius: '0.75rem',
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/role/roles/${id}`, {
          method: 'DELETE',
          headers: headers(),
        });

        if (res.ok) {
          toast.success('The role has been deleted');
          setRoles((prev) => prev.filter((role) => role._id !== id));
        } else {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to delete');
        }
      } catch (err) {
        toast.error(err.message || 'Failed to delete role');
      } finally {
        setLoading(false);
      }
    }
  };

  // Excel Export
  const exportToExcel = () => {
    if (roles.length === 0) {
      toast.warn('No roles to export');
      return;
    }

    const exportData = filtered.map((r, i) => ({
      'S.No': i + 1,
      'Role Name': r.roleName || '',
      'Description': r.description || '',
      'Status': 'Active',
      'Created Date': r.createdAt ? new Date(r.createdAt).toLocaleDateString() : 'N/A',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Roles');
    XLSX.writeFile(workbook, `Role_Master_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Role master exported to Excel!');
  };

  const totalRoles = roles.length;
  const totalStaticMenus = STATIC_MENUS.length;
  const totalDropdownMenus = dropdownSections.reduce((acc, sec) => acc + sec.items.length, 0);
  const totalModules = totalStaticMenus + totalDropdownMenus;

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Page Title & Breadcrumb Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
            <HiOutlineShieldCheck className="text-blue-600 text-3xl" />
            Role & Permission Master
          </h2>
          <p className="text-xs md:text-sm text-gray-500 mt-1">
            Define system roles, access levels, and granular permissions for all software modules
          </p>
        </div>

        <button
          onClick={() => openModal()}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow transition-all duration-150 flex items-center gap-2 text-sm uppercase tracking-wide cursor-pointer w-fit"
        >
          <MdAdd size={20} /> Add New Role
        </button>
      </div>

      {/* KPI Summary Quick Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center text-2xl font-bold">
            <HiOutlineKey />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Defined Roles</p>
            <p className="text-2xl font-extrabold text-gray-900">{totalRoles}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-green-50 text-green-600 flex items-center justify-center text-2xl font-bold">
            <MdCheckCircle />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Active Roles</p>
            <p className="text-2xl font-extrabold text-green-600">{totalRoles}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center text-2xl font-bold">
            <MdSecurity />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Total Menus</p>
            <p className="text-2xl font-extrabold text-purple-600">{totalModules}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center text-2xl font-bold">
            <HiOutlineUserGroup />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Permission Scope</p>
            <p className="text-2xl font-extrabold text-indigo-600">4 Actions</p>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5 md:p-6">
        {/* Unified Control Toolbar */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 pb-5 border-b border-gray-200">
          {/* Search bar */}
          <div className="relative flex-1 max-w-lg">
            <MdSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search by role name or description..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <MdClear size={18} />
              </button>
            )}
          </div>

          {/* Controls: Per Page, View Switcher & Excel Export */}
          <div className="flex items-center flex-wrap gap-2.5">
            {/* Entries per page */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500">Show:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="py-2 px-2.5 bg-gray-50 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={6}>6 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 p-1 rounded-lg border border-gray-200">
              <button
                type="button"
                onClick={() => setViewMode('card')}
                className={`p-1.5 rounded-md flex items-center gap-1.5 text-xs font-medium transition cursor-pointer ${
                  viewMode === 'card'
                    ? 'bg-white text-blue-600 shadow-sm font-bold'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Landscape Card View"
              >
                <HiOutlineViewGrid size={18} />
                <span className="hidden sm:inline">Cards</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`p-1.5 rounded-md flex items-center gap-1.5 text-xs font-medium transition cursor-pointer ${
                  viewMode === 'table'
                    ? 'bg-white text-blue-600 shadow-sm font-bold'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Table List View"
              >
                <HiOutlineViewList size={18} />
                <span className="hidden sm:inline">Table</span>
              </button>
            </div>

            {/* Export Excel Button */}
            <button
              onClick={exportToExcel}
              className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-sm transition cursor-pointer"
              title="Export Roles to Excel"
            >
              <MdFileDownload size={18} />
              <span className="hidden sm:inline">Export Excel</span>
            </button>
          </div>
        </div>

        {/* Content Area */}
        {loading ? (
          <div className="text-center py-16 text-gray-500">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent mb-3"></div>
            <p className="text-sm font-medium">Loading roles and permissions...</p>
          </div>
        ) : paginated.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <HiOutlineShieldCheck className="mx-auto text-5xl text-gray-300 mb-2" />
            <p className="text-gray-700 font-bold text-base">No roles found</p>
            <p className="text-gray-500 text-xs mt-1">Try adjusting your search criteria or create a new role.</p>
            <button
              onClick={() => openModal()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition"
            >
              Add New Role
            </button>
          </div>
        ) : viewMode === 'card' ? (
          /* LANDSCAPE CARD VIEW */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {paginated.map((r) => (
              <div
                key={r._id}
                className="bg-white rounded-xl border border-gray-200 hover:border-blue-400 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between overflow-hidden"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-gray-50 to-blue-50/40 px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold text-lg shadow-sm shrink-0">
                      <FaShieldAlt size={18} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-extrabold text-gray-900 truncate uppercase">
                        {r.roleName}
                      </h3>
                      <span className="inline-block text-[10px] font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full mt-0.5">
                        ACTIVE ROLE
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => openViewModal(r)}
                      className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-white rounded-lg transition"
                      title="View Role Access"
                    >
                      <MdVisibility size={18} />
                    </button>
                    <button
                      onClick={() => openModal(r)}
                      className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-white rounded-lg transition"
                      title="Edit Permissions"
                    >
                      <MdEdit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(r._id, r.roleName)}
                      className="p-1.5 text-red-600 hover:text-red-800 hover:bg-white rounded-lg transition"
                      title="Delete Role"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div className="p-5 space-y-3.5 flex-1">
                  <div>
                    <span className="text-[10px] font-bold uppercase text-gray-400 block mb-1">
                      Role Description
                    </span>
                    <p className="text-xs text-gray-700 line-clamp-2 italic bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                      {r.description || 'No detailed description provided for this role.'}
                    </p>
                  </div>

                  {/* Module Scope Preview */}
                  <div>
                    <span className="text-[10px] font-bold uppercase text-gray-400 block mb-1.5">
                      Configured Access Scopes
                    </span>
                    <div className="flex flex-wrap gap-1.5 text-[10px]">
                      <span className="bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded font-semibold">
                        Masters
                      </span>
                      <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded font-semibold">
                        Manifests
                      </span>
                      <span className="bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded font-semibold">
                        Transactions
                      </span>
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded font-semibold">
                        Reports
                      </span>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-5 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                  <span>Full Permission Matrix</span>
                  <button
                    onClick={() => openViewModal(r)}
                    className="text-blue-600 hover:text-blue-800 font-bold cursor-pointer"
                  >
                    Inspect Matrix &rarr;
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* TABLE LIST VIEW */
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full table-auto border-collapse">
              <thead className="bg-blue-600 text-white uppercase text-xs font-semibold tracking-wider">
                <tr>
                  <th className="p-3.5 text-left">S.No</th>
                  <th className="p-3.5 text-left">Role Name</th>
                  <th className="p-3.5 text-left">Description</th>
                  <th className="p-3.5 text-left">Module Access</th>
                  <th className="p-3.5 text-left">Status</th>
                  <th className="p-3.5 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-xs">
                {paginated.map((r, i) => {
                  const sNo = (currentPage - 1) * pageSize + i + 1;
                  return (
                    <tr key={r._id} className="hover:bg-blue-50/50 transition">
                      <td className="p-3.5 font-bold text-gray-500">{sNo}</td>
                      <td className="p-3.5 font-bold text-blue-700 uppercase">{r.roleName}</td>
                      <td className="p-3.5 text-gray-700 max-w-xs truncate">{r.description || '-'}</td>
                      <td className="p-3.5">
                        <div className="flex flex-wrap gap-1">
                          <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-[10px] font-bold">
                            Masters
                          </span>
                          <span className="bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded text-[10px] font-bold">
                            Manifest
                          </span>
                          <span className="bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded text-[10px] font-bold">
                            Reports
                          </span>
                        </div>
                      </td>
                      <td className="p-3.5">
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-green-100 text-green-700">
                          ACTIVE
                        </span>
                      </td>
                      <td className="p-3.5 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => openViewModal(r)}
                            className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            title="View Role Access"
                          >
                            <MdVisibility size={18} />
                          </button>
                          <button
                            onClick={() => openModal(r)}
                            className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition"
                            title="Edit Permissions"
                          >
                            <MdEdit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(r._id, r.roleName)}
                            className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition"
                            title="Delete Role"
                          >
                            <MdDelete size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {filtered.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200 text-xs text-gray-600">
            <div>
              Showing{' '}
              <span className="font-bold text-gray-800">
                {(currentPage - 1) * pageSize + 1}
              </span>{' '}
              to{' '}
              <span className="font-bold text-gray-800">
                {Math.min(currentPage * pageSize, filtered.length)}
              </span>{' '}
              of <span className="font-bold text-gray-800">{filtered.length}</span> entries
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 rounded-lg font-semibold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((p, idx, arr) => {
                  const prevVal = arr[idx - 1];
                  return (
                    <React.Fragment key={p}>
                      {prevVal && p - prevVal > 1 && <span className="px-1 text-gray-400">...</span>}
                      <button
                        onClick={() => setCurrentPage(p)}
                        className={`px-3 py-1.5 rounded-lg font-bold transition cursor-pointer ${
                          currentPage === p
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded-lg font-semibold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CREATE / EDIT PERMISSION MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 md:p-8 relative border border-gray-200">
            <button
              onClick={() => setModalOpen(false)}
              className="absolute top-5 right-5 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
            >
              <FaTimes size={22} />
            </button>

            <div className="border-b border-gray-200 pb-4 mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <HiOutlineKey className="text-blue-600" />
                {editingId ? 'Edit Role & Permissions' : 'Create New System Role'}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Configure role identity and assign granular menu-level CRUD permissions
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Role Name *
                </label>
                <input
                  required
                  placeholder="e.g. Branch Manager, Accounts Officer"
                  value={form.roleName}
                  onChange={(e) => setForm({ ...form, roleName: e.target.value })}
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Description
                </label>
                <input
                  placeholder="e.g. Full operational access for branch level activities"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-800 uppercase text-xs tracking-wider border-l-4 border-blue-600 pl-2">
                  Access Control List (ACL)
                </h3>
                <span className="text-[10px] text-gray-400 font-bold uppercase italic">
                  * Check green box to select all actions for that menu
                </span>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 max-h-[420px] overflow-y-auto space-y-6">
                {/* STATIC MENUS SECTION */}
                <div>
                  <div className="bg-blue-600 p-2 rounded-lg font-bold text-white text-xs mb-3 uppercase tracking-wider shadow-sm flex items-center justify-between">
                    <span>Main System Menus</span>
                    <span className="text-[10px] opacity-80">{STATIC_MENUS.length} Menus</span>
                  </div>
                  <div className="space-y-2">
                    {STATIC_MENUS.map((menuName) => renderMenuRow(menuName))}
                  </div>
                </div>

                {/* DROPDOWN SECTIONS */}
                {dropdownSections.map((section) => (
                  <div key={section.id}>
                    <div className="bg-blue-600 p-2 rounded-lg font-bold text-white text-xs mb-3 uppercase tracking-wider shadow-sm flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <section.icon /> {section.title}
                      </div>
                      <span className="text-[10px] opacity-80">{section.items.length} Menus</span>
                    </div>
                    <div className="space-y-2">
                      {section.items.map((item) => renderMenuRow(item.label))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 border-t border-gray-200 pt-5">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold text-xs uppercase transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={loading}
                className="px-8 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-xs uppercase shadow-md transition disabled:opacity-60 cursor-pointer"
              >
                {loading ? 'Saving...' : 'Save Permissions'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIEW ROLE DETAILS MODAL */}
      {viewModalOpen && viewingRole && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-gray-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white font-bold text-lg">
                  <FaShieldAlt />
                </div>
                <div>
                  <h3 className="font-extrabold text-lg text-white uppercase">
                    {viewingRole.roleName}
                  </h3>
                  <p className="text-xs text-blue-100">
                    {viewingRole.description || 'System access matrix inspection'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewModalOpen(false)}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition cursor-pointer"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-5">
              <div className="flex items-center justify-between bg-blue-50/50 p-3.5 rounded-xl border border-blue-100 text-xs">
                <div>
                  <span className="font-bold text-gray-500 uppercase block text-[10px]">Role Status</span>
                  <span className="font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full text-[10px]">
                    ACTIVE
                  </span>
                </div>
                <div className="text-right">
                  <span className="font-bold text-gray-500 uppercase block text-[10px]">Authorized Actions</span>
                  <span className="font-bold text-blue-700">View, Add, Edit, Delete</span>
                </div>
              </div>

              {/* Granted Permissions List */}
              <div className="border border-gray-200 rounded-xl p-4 max-h-[380px] overflow-y-auto bg-gray-50 space-y-4">
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                  Configured Privileges by Module
                </h4>

                {[...STATIC_MENUS, ...dropdownSections.flatMap((s) => s.items.map((i) => i.label))].map(
                  (menuName) => {
                    const perms = viewingPermissions[menuName] || {};
                    const hasAny = perms.view || perms.add || perms.edit || perms.delete;

                    return (
                      <div
                        key={menuName}
                        className={`p-2.5 rounded-lg border text-xs flex items-center justify-between ${
                          hasAny
                            ? 'bg-white border-blue-200 shadow-2xs'
                            : 'bg-gray-100/60 border-gray-200 opacity-60'
                        }`}
                      >
                        <span className="font-bold text-gray-800">{menuName}</span>
                        <div className="flex items-center gap-1.5">
                          {['view', 'add', 'edit', 'delete'].map((action) => (
                            <span
                              key={action}
                              className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${
                                perms[action]
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-200 text-gray-400'
                              }`}
                            >
                              {action}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-200 rounded-b-2xl">
              <button
                onClick={() => {
                  setViewModalOpen(false);
                  openModal(viewingRole);
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold uppercase transition flex items-center gap-1.5 cursor-pointer"
              >
                <MdEdit size={16} /> Edit Privileges
              </button>
              <button
                onClick={() => setViewModalOpen(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-xs font-bold uppercase transition cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleMaster;